import { Asset as ContentfulAsset } from 'contentful';

import { IFullwidthHero20Fields } from '@/types/contentful';

import { usePageDataContext } from '../../../context/PageData';
import { getAssetFromOptions } from '../../../utils/contentful';

import CtaWidget from '../../CtaWidget';
import PrivacyPolicyDisclaimer from '../../PrivacyPolicyDisclaimer';
import { TextWithOptions } from '../../Text/TextWithOptions';

import contentfulColorToHexColor from '~/utils/contentfulColorToHexColor';
import { Eyebrow } from '../../Eyebrow';
import { SocialProofBar } from '../../SocialProofBar';
import { AdditionalText } from '../AdditionalText';
import DesktopImageDisclaimer from '../DesktopImageDisclaimer';
import { Asset as AssetComponent } from '../../AtomicComponents/Asset';
import { NewVideoPlayer } from '~/components/NewVideoPlayer';

import cx from 'classnames';

interface Props extends IFullwidthHero20Fields {
  isReturning?: boolean;
}

export default function DesktopView({
  headingText,
  headingTextOptions,
  useLightHeadingText,
  subheadingText,
  subheadingTextOptions,
  fullwidthDesktopImage,
  desktopImageOptions,
  desktopImageDisclaimer,
  additionalText,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  desktopCta,
  videoModule,
  eyebrow,
  socialProofBar,
  isReturning,
  returningUserHeadingText,
  backgroundColor,
  desktopPrivacyPolicyDisclaimerColor
}: Props) {
  const visibility = fullwidthDesktopImage ? 'visible' : 'hidden';
  const {
    cookies: { utm_source = '' } = {},
    advocateUserData,
    query: { gl_content, g_adgroupid } = {}
  } = usePageDataContext();

  const hasVideo =
    videoModule?.fields?.vimeoId || videoModule?.fields?.mp4video;

  return (
    <section
      style={{
        backgroundColor: backgroundColor?.fields
          ? contentfulColorToHexColor(backgroundColor.fields)
          : 'initial'
      }}
      data-blockid="fullwidth-hero-desktop"
      className="desktop-block-container mt-container tablet:mt-3"
      data-e2e="hero"
      data-testid="fullwidth-hero-desktop"
    >
      <div className="layout layout-padding mt-2 box-border w-full flex-1 items-center justify-center">
        <div className="flex w-full items-center justify-center gap-8 px-15 py-8">
          <div className="flex w-full flex-1 flex-col items-center">
            <div className="box-border flex flex-col items-start justify-center gap-10">
              <div className="flex max-w-[532px] flex-col items-start gap-5">
                {eyebrow && (
                  <Eyebrow
                    eyebrowFields={eyebrow.fields}
                    advocateData={advocateUserData}
                  />
                )}
                <TextWithOptions
                  as="h1"
                  v2
                  className={cx(
                    'self-stretch text-left text-[52px] leading-[115%]',
                    useLightHeadingText ? 'text-white' : 'text-black'
                  )}
                  keyword={gl_content}
                  channel={utm_source}
                  adGroupId={g_adgroupid}
                  fallback={
                    (isReturning
                      ? returningUserHeadingText ?? headingText
                      : headingText) || ''
                  }
                  options={headingTextOptions}
                  module="Fullwidth Hero heading"
                  data-testid="fullwidth-hero-headline-desktop"
                />
                <TextWithOptions
                  as="p"
                  v2
                  className={`self-stretch text-left text-[20px] leading-[32px] ${
                    useLightHeadingText ? 'text-white' : 'text-black'
                  }`}
                  data-testid="fullwidth-hero-subhead-desktop"
                  keyword={gl_content}
                  channel={utm_source}
                  adGroupId={g_adgroupid}
                  fallback={subheadingText ?? ''}
                  options={subheadingTextOptions}
                  module="Fullwidth Hero subheading"
                />
              </div>
              <div className="flex max-w-[350px] flex-col items-start gap-3 p-0">
                {desktopCta && (
                  <CtaWidget
                    {...desktopCta?.fields}
                    sysId={desktopCta?.sys?.contentType?.sys?.id}
                    id="fullwidth-desktop-hero-cta"
                    module="Hero"
                    height={56}
                    isMobile={false}
                    responsive
                    width={'100%'}
                  />
                )}
                {additionalText && (
                  <AdditionalText additionalText={additionalText} />
                )}
                {privacyPolicyDisclaimer && (
                  <div className="box-border">
                    <PrivacyPolicyDisclaimer
                      privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                      displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                      colorHex={desktopPrivacyPolicyDisclaimerColor}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-1 flex-col items-center gap-3">
            {hasVideo ? (
              <NewVideoPlayer
                sysId={videoModule?.sys?.id}
                title={videoModule?.fields?.title || ''}
                theme={videoModule?.fields?.theme || 'light'}
                thumbnail={videoModule?.fields?.fallbackImage}
                video={videoModule?.fields?.mp4video}
                playAudio={!videoModule?.fields?.muted}
                autoplay={videoModule?.fields?.autoplay}
                loop={videoModule?.fields?.loop}
                inHero
              />
            ) : (
              <div className="relative box-border flex w-full flex-1 flex-col gap-3">
                <AssetComponent
                  className="h-full max-h-[630px] max-w-[630px] overflow-hidden rounded-4xl"
                  asset={getAssetFromOptions(
                    gl_content,
                    utm_source,
                    g_adgroupid,
                    fullwidthDesktopImage as ContentfulAsset,
                    desktopImageOptions
                  )}
                  loading="eager"
                  style={{ visibility }}
                />
                {desktopImageDisclaimer && (
                  <div className="box-border">
                    <DesktopImageDisclaimer
                      desktopImageDisclaimer={desktopImageDisclaimer}
                      colorHex={useLightHeadingText ? '#FFFFFF' : '#1D252C'}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {socialProofBar && <SocialProofBar {...socialProofBar.fields} />}
      </div>
    </section>
  );
}
